import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, ChevronLeft, ChevronRight, Download, Filter, X, ShieldAlert } from "lucide-react";
import api, { readError } from "@/lib/api";

const PAGE_SIZE = 25;

const ACTION_COLORS = {
  LOGIN_SUCCESS: "default", LOGIN_FAIL: "destructive",
  LOGIN_2FA_FAIL: "destructive", LOGIN_2FA_CHALLENGE: "secondary",
  "2FA_ENABLED": "default", "2FA_DISABLED": "secondary",
  "2FA_FORCE_DISABLED": "destructive",
  RECOVERY_USED: "destructive", RECOVERY_TRIGGERED: "secondary",
  RECOVERY_REQUEST_REJECTED: "outline",
  PASSWORD_RESET: "secondary", PASSWORD_RESET_REQUESTED: "outline",
  ADMIN_BOOTSTRAPPED: "default", "2FA_SETUP_STARTED": "outline",
  INQUIRY_SUBMITTED: "secondary", INQUIRY_STATUS_CHANGED: "outline",
  ORDER_STATUS_CHANGED: "outline", SMTP_TEST_SENT: "outline",
  USER_INVITED: "outline", SETTINGS_UPDATED: "outline",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("hr-HR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  } catch { return iso; }
}

const FILTER_DEFAULTS = { action: "", actor_email: "", since: "", until: "" };

export default function AdminAuditLog() {
  const [state, setState] = useState({ items: null, total: 0, skip: 0 });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [securityOnly, setSecurityOnly] = useState(false);
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [applied, setApplied] = useState(FILTER_DEFAULTS); // server-applied snapshot

  // Fetch distinct actions for the dropdown
  useEffect(() => {
    api.get("/admin/audit-logs/actions").then((r) => setActions(r.data?.actions || [])).catch(() => {});
  }, []);

  const fetchPage = useCallback(async (skip = 0, withFilters = applied, sec = securityOnly) => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: PAGE_SIZE, skip, security_only: sec || undefined };
      Object.entries(withFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/audit-logs", { params });
      setState({ items: data.items, total: data.total, skip });
    } catch (err) {
      setError(readError(err, "Could not load audit log."));
    } finally {
      setLoading(false);
    }
  }, [applied, securityOnly]);

  useEffect(() => { fetchPage(0, applied, securityOnly); }, [fetchPage, applied, securityOnly]);

  const apply = () => setApplied({ ...filters });
  const clear = () => { setFilters(FILTER_DEFAULTS); setApplied(FILTER_DEFAULTS); };
  const downloadCsv = async () => {
    try {
      const params = { security_only: securityOnly || undefined };
      Object.entries(applied).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/audit-logs/export.csv", { params, responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(readError(err, "CSV export failed."));
    }
  };

  const { items, total, skip } = state;
  const page = Math.floor(skip / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filterCount = useMemo(() => Object.values(applied).filter(Boolean).length + (securityOnly ? 1 : 0), [applied, securityOnly]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-audit-log">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary">Admin</div>
        <h1 className="mt-1 font-serif text-4xl text-foreground">Audit log</h1>
        <p className="mt-2 text-sm text-muted-foreground">Every sensitive event recorded. Newest first.</p>
      </div>

      <Tabs value={securityOnly ? "security" : "all"} onValueChange={(v) => setSecurityOnly(v === "security")}>
        <TabsList data-testid="audit-tabs">
          <TabsTrigger value="all" data-testid="audit-tab-all">All events</TabsTrigger>
          <TabsTrigger value="security" data-testid="audit-tab-security">
            <ShieldAlert className="h-3 w-3 mr-1" strokeWidth={2} /> Security events
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filter bar */}
      <Card className="bg-card border-border/60 shadow-ambient">
        <CardContent className="p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Action</Label>
            <Select value={filters.action || "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, action: v === "__all" ? "" : v }))}>
              <SelectTrigger data-testid="filter-action" className="h-9"><SelectValue placeholder="Any action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Any action</SelectItem>
                {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Actor email</Label>
            <Input data-testid="filter-actor" value={filters.actor_email} onChange={(e) => setFilters((f) => ({ ...f, actor_email: e.target.value }))} placeholder="user@…" className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">From</Label>
            <Input data-testid="filter-since" type="date" value={filters.since} onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">To</Label>
            <Input data-testid="filter-until" type="date" value={filters.until} onChange={(e) => setFilters((f) => ({ ...f, until: e.target.value }))} className="h-9" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="filter-apply" size="sm" onClick={apply}>
              <Filter className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Apply
            </Button>
            <Button data-testid="filter-clear" size="sm" variant="outline" onClick={clear} disabled={!filterCount}>
              <X className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Clear
            </Button>
            <Button data-testid="csv-export-btn" size="sm" variant="outline" onClick={downloadCsv}>
              <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/60 shadow-ambient overflow-hidden">
        <CardContent className="p-0">
          {items === null || loading ? (
            <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-destructive" data-testid="audit-error">{error}</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground" data-testid="audit-empty">No events match.</div>
          ) : (
            <Table data-testid="audit-table">
              <TableHeader>
                <TableRow className="bg-muted/40 border-border/60">
                  <TableHead className="py-4 px-6 text-foreground/80">When</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80">Action</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80">Actor</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80">Target</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80">IP</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} data-testid={`audit-row-${row.id}`} className="border-border/60 hover:bg-muted/20 align-top">
                    <TableCell className="py-4 px-6 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(row.created_at)}</TableCell>
                    <TableCell className="py-4 px-6">
                      <Badge variant={ACTION_COLORS[row.action] || "outline"} className="font-mono text-[11px]">{row.action}</Badge>
                    </TableCell>
                    <TableCell className="py-4 px-6 text-sm">{row.actor_email || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="py-4 px-6 text-sm">{row.target_email || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="py-4 px-6 text-xs text-muted-foreground font-mono">{row.ip || "—"}</TableCell>
                    <TableCell className="py-4 px-6 text-xs text-muted-foreground font-mono break-all max-w-xs">
                      {row.metadata && Object.keys(row.metadata).length
                        ? Object.entries(row.metadata).map(([k, v]) => `${k}: ${v}`).join(", ")
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {items && items.length > 0 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Page <span className="text-foreground font-medium">{page}</span> of {pages} · {total} total {filterCount ? `(filtered)` : ""}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={loading || skip === 0} onClick={() => fetchPage(Math.max(0, skip - PAGE_SIZE), applied, securityOnly)} data-testid="audit-prev-btn">
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={loading || skip + PAGE_SIZE >= total} onClick={() => fetchPage(skip + PAGE_SIZE, applied, securityOnly)} data-testid="audit-next-btn">
              Next <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
